import { LitElement, html } from 'lit';
import PartnersRegistrationApi from '../../../shared/api/PartnersRegistrationApi';
import AppContextConsumer from '../../../shared/context/app-context/AppContextConsumer';
import FormController from '../../../shared/controllers/FormController';
import styles from './styles';
import '../../../shared/components/button/Button';
import '../../../shared/components/form/Form';
import '../../../shared/components/form-group/FormGroup';
import '../../../shared/components/form-feedback/FormFeedback';
import '../../../shared/components/form-error/FormError';
import '../../../shared/components/input/Input';
import '../../../shared/components/select/Select';
import schema from '../../../../../schemas/PartnersRegistrationSchema';
import messages from '../../../../../schemas/PartnersRegistrationSchema/messages';
import openPartnersSchema from '../../../../../schemas/OpenPartnersRegistrationSchema';
import openPartnersMessages from '../../../../../schemas/OpenPartnersRegistrationSchema/messages';

class PartnersRegistrationForm extends LitElement {
  static styles = styles;

  static properties = {
    type: { type: String },
    options: { type: Array, state: true },
  };

  constructor() {
    super();
    this.type = 'partners';
    this.messages = messages();
    this.openPartnersMessages = openPartnersMessages();

    this.partnersOptions = [
      { value: 'Marketing', label: 'Marketing' },
      { value: 'Sales', label: 'Sales' },
      { value: 'Admin', label: 'Admin' },
      { value: 'Technical', label: 'Technical' },
      { value: 'MSSP Technical', label: 'MSSP Technical' },
      { value: 'MSSP Sales', label: 'MSSP Sales' },
    ];

    this.openPartnersOptions = [
      { value: '', label: 'Select a role', placeholder: true, disabled: true },
      { value: 'Marketing', label: 'Marketing' },
      { value: 'Business', label: 'Business' },
      { value: 'Technical', label: 'Technical' },
    ];

    this.options = this.partnersOptions;
  }

  context = AppContextConsumer(this);

  form = new FormController(this, { onSubmit: (params) => this.onSubmit(params) });

  connectedCallback() {
    super.connectedCallback();

    const isOpenPartners = this.context.value?.type === 'openPartners';
    this.options = isOpenPartners ? this.openPartnersOptions : this.partnersOptions;
    this.form.schema = isOpenPartners ? openPartnersSchema(this.openPartnersMessages) : schema(this.messages);
  }

  #escapeHTML(text) {
    const escape = document.createElement('textarea');
    escape.textContent = text;
    return escape.innerHTML;
  }

  #buildPayload(values) {
    const base = {
      FirstName: this.#escapeHTML(values.firstName),
      LastName: this.#escapeHTML(values.lastName),
      Email: this.#escapeHTML(values.email),
      JobRole: values.jobRole,
      CompanyName: this.#escapeHTML(values.companyName),
      SourceIdentifier: values.sourceIdentifier,
    };

    return {
      ...base,
      ...(this.type === 'openPartners' && { Title: this.#escapeHTML(values.title) }),
      PartnerID: this.#escapeHTML(values.partnerId),
    };
  }

  async onSubmit({ values }) {
    const isOpenPartners = this.type === 'openPartners';
    const api = new PartnersRegistrationApi({
      env: this.context.value?.env,
      ...(isOpenPartners && { endpoint: '/evaluations/api/v1/open-partners/accounts' }),
    });
    const payload = this.#buildPayload(values);

    try {
      await api.create(payload);
    } catch (error) {
      const msg = `Oops! ${error.message || 'Server Error'}`;
      throw new Error(msg);
    }

    this.dispatchEvent(new CustomEvent('next-step', { composed: true, bubbles: true }));
  }

  render() {
    const isOpenPartners = this.context.value?.type === 'openPartners';
    const idFieldLabel = isOpenPartners ? 'Customer ID' : 'Partner ID';
    const sourceIdentifierValue = isOpenPartners ? 'openPartners' : 'partners';
    const description = isOpenPartners
      ? 'Register to access the Tenable OPEN partner portal for a one-stop view of joint marketing benefits, developer guides, product access and technical documentation.'
      : 'Register to access the Tenable Partner Portal for a one-stop view of sales and technical enablement, demand generation marketing, pipeline management, and analytics.';

    return html`
      <div class="partners-registration-form-container">
        <div id="partners-registration-form-desc">
          <p>${description}</p>
        </div>

        ${this.form.isSubmitting ? html`
          <div id="partners-registration-form-submit-msg" class="alert alert-info submit-msg">
            <strong>Submitting:</strong><br>
            Please allow up to 2 minutes for request to process, do not refresh the page
          </div>` : ''}

        <tenable-form id="partners-registration-form" .errors=${this.form.errors} .values=${this.form.values} @form-submitted=${(e) => this.form.submitHandler(e)}>
          <tenable-form-error id="partners-registration-form-error-msg" .errors=${this.form.formErrors}></tenable-form-error>

          <tenable-input type="hidden" id="partners-sourceIdentifier" name="sourceIdentifier" value="${sourceIdentifierValue}" @input-change=${(e) => this.form.inputHandler(e)}></tenable-input>

          <tenable-form-group>
            <tenable-input type="text" id="partners-firstName" name="firstName" label="First Name" placeholder="First Name" ?invalid=${this.form.errors.firstName} @input-change=${(e) => this.form.inputHandler(e)} @input-blur=${(e) => this.form.validateField(e)} required></tenable-input>
            <tenable-form-feedback message=${this.form.errors.firstName}></tenable-form-feedback>
          </tenable-form-group>

          <tenable-form-group>
            <tenable-input type="text" id="partners-lastName" name="lastName" label="Last Name" placeholder="Last Name" ?invalid=${this.form.errors.lastName} @input-change=${(e) => this.form.inputHandler(e)} @input-blur=${(e) => this.form.validateField(e)} required></tenable-input>
            <tenable-form-feedback message=${this.form.errors.lastName}></tenable-form-feedback>
          </tenable-form-group>

          <tenable-form-group>
            <tenable-input type="email" id="partners-email" name="email" label="Email" placeholder="Email" ?invalid=${this.form.errors.email} @input-change=${(e) => this.form.inputHandler(e)} @input-blur=${(e) => this.form.validateField(e)} required></tenable-input>
            <tenable-form-feedback message=${this.form.errors.email}></tenable-form-feedback>
          </tenable-form-group>

          ${isOpenPartners ? html`
            <tenable-form-group>
              <tenable-input type="text" id="partners-title" name="title" label="Title" placeholder="Title" ?invalid=${this.form.errors.title} @input-change=${(e) => this.form.inputHandler(e)} @input-blur=${(e) => this.form.validateField(e)} required></tenable-input>
              <tenable-form-feedback message=${this.form.errors.title}></tenable-form-feedback>
            </tenable-form-group>
          ` : ''}

          <tenable-form-group>
            <label for="jobRole">Job Role ${isOpenPartners ? '' : html`<small>(To select multiple roles use ctrl or cmd)</small>`}</label>
            <tenable-select
              name="jobRole"
              id="partners-jobRole"
              ?invalid=${this.form.errors.jobRole}
              @input-change=${(e) => this.form.inputHandler(e)}
              @input-blur=${(e) => this.form.validateField(e)}
              .options=${this.options}
              size=${isOpenPartners ? undefined : this.options.length}
              ?multiple=${!isOpenPartners}
              required
            >
            </tenable-select>
            <tenable-form-feedback message=${this.form.errors.jobRole}></tenable-form-feedback>
          </tenable-form-group>

          <tenable-form-group>
            <label for="partnerId" style="margin-bottom: 0;">${idFieldLabel}</label>
            ${isOpenPartners
    ? html`<small>If you do not know your customer ID, please email <a href="mailto:techpartners@tenable.com">techpartners@tenable.com</a> to request it</small>`
    : html`<small>If you do not know your partner ID please ask your channel manager or email <a href="mailto:partners@tenable.com">partners@tenable.com</a></small>`}
            <tenable-input type="number" inputmode="numeric" id="partners-partnerId" name="partnerId" placeholder="${idFieldLabel}" ?invalid=${this.form.errors.partnerId} hide-spinner @input-change=${(e) => this.form.inputHandler(e)} @input-blur=${(e) => this.form.validateField(e)} required></tenable-input>
            <tenable-form-feedback message=${this.form.errors.partnerId}></tenable-form-feedback>
          </tenable-form-group>

          <tenable-form-group>
            <tenable-input type="text" id="partners-company" name="companyName" label="Company" placeholder="Company" ?invalid=${this.form.errors.company} @input-change=${(e) => this.form.inputHandler(e)} @input-blur=${(e) => this.form.validateField(e)} required></tenable-input>
            <tenable-form-feedback message=${this.form.errors.companyName}></tenable-form-feedback>
          </tenable-form-group>

          <tenable-button type="submit" class="registration-btn" size="lg" ?disabled=${this.form.isSubmitting}>
            ${this.form.isSubmitting ? 'Submitting...' : 'Submit'}
          </tenable-button>
        </tenable-form>
      </div>
    `;
  }
}

customElements.define('tenable-partners-registration-form', PartnersRegistrationForm);
